import { piconerosToXmrDecimal, piconerosToMillineroDecimal, MONERO_ADDR_RE } from '@/api/monero/uri'

export { piconerosToXmrDecimal, piconerosToMillineroDecimal as piconerosToMXmrDecimal }

export const abbrNum = n => {
  if (n < 1e4) return n
  if (n >= 1e4 && n < 1e6) return +(n / 1e3).toFixed(1) + 'k'
  if (n >= 1e6 && n < 1e9) return +(n / 1e6).toFixed(1) + 'm'
  if (n >= 1e9 && n < 1e12) return +(n / 1e9).toFixed(1) + 'b'
  if (n >= 1e12) return +(n / 1e12).toFixed(1) + 't'
}

export function suffix (n) {
  const j = n % 10
  const k = n % 100
  if (j === 1 && k !== 11) {
    return n + 'st'
  }
  if (j === 2 && k !== 12) {
    return n + 'nd'
  }
  if (j === 3 && k !== 13) {
    return n + 'rd'
  }
  return n + 'th'
}

/**
 * Take a number that represents a count
 * and return a formatted label e.g. 0 sats, 1 sat, 2 sats
 *
 * @param n The number of sats
 * @param opts Options
 * @param opts.abbreviate Whether to abbreviate the number
 * @param opts.unitSingular The singular unit label
 * @param opts.unitPlural The plural unit label
 * @param opts.format Format the number with `Intl.NumberFormat`. Can only be used if `abbreviate` is false
 */
export const numWithUnits = (n, {
  abbreviate = true,
  unitSingular = 'sat',
  unitPlural = 'sats',
  format
} = {}) => {
  if (isNaN(n)) {
    return `${n} ${unitPlural}`
  }
  return `${abbreviate ? abbrNum(n) : !abbreviate || format === true ? new Intl.NumberFormat().format(n) : n} ${n === 1 ? unitSingular : unitPlural}`
}

export const fixedDecimal = (n, f) => {
  return Number.parseFloat(n).toFixed(f)
}

export const ceilBigInt = (n, d) => {
  return n / d + (n % d ? 1n : 0n)
}

export const hexToB64 = hexstring => {
  return btoa(hexstring.match(/\w{2}/g).map(function (a) {
    return String.fromCharCode(parseInt(a, 16))
  }).join(''))
}

// some base64 encoders get fancy and remove padding
export const ensureB64Padding = str => {
  return str + Array((4 - str.length % 4) % 4 + 1).join('=')
}

export const B64_REGEX = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/
export const B64_URL_REGEX = /^(?:[A-Za-z0-9_-]{4})*(?:[A-Za-z0-9_-]{2}[.=]{2}|[A-Za-z0-9_-]{3}[.=])?$/
export const HEX_REGEX = /^[0-9a-fA-F]+$/

export const ensureB64 = hexOrB64Url => {
  if (HEX_REGEX.test(hexOrB64Url)) {
    hexOrB64Url = hexToB64(hexOrB64Url)
  }

  hexOrB64Url = ensureB64Padding(hexOrB64Url)

  // some folks use url-safe base64
  if (B64_URL_REGEX.test(hexOrB64Url)) {
    // Convert from URL-safe base64 to regular base64
    hexOrB64Url = hexOrB64Url.replace(/-/g, '+').replace(/_/g, '/').replace(/\./g, '=')
    switch (hexOrB64Url.length % 4) {
      case 2: hexOrB64Url += '=='; break
      case 3: hexOrB64Url += '='; break
    }
  }

  if (B64_REGEX.test(hexOrB64Url)) {
    return hexOrB64Url
  }

  throw new Error('not a valid hex or base64 url or base64 encoded string')
}

export function giveOrdinalSuffix (i) {
  const j = i % 10
  const k = i % 100
  if (j === 1 && k !== 11) {
    return i + 'st'
  }
  if (j === 2 && k !== 12) {
    return i + 'nd'
  }
  if (j === 3 && k !== 13) {
    return i + 'rd'
  }
  return i + 'th'
}

// check if something is _really_ a number.
// returns true for every number in this range: [-Infinity, ..., 0, ..., Infinity]
export const isNumber = x => typeof x === 'number' && !Number.isNaN(x)

/**
 *
 * @param {any | bigint} x
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export const toNumber = (x, min = Number.MIN_SAFE_INTEGER, max = Number.MAX_SAFE_INTEGER) => {
  if (typeof x === 'undefined') {
    throw new Error('value is required')
  }
  if (typeof x === 'bigint') {
    if (x < BigInt(min) || x > BigInt(max)) {
      throw new Error(`value ${x} must be between ${min} and ${max}`)
    }
    return Number(x)
  } else {
    const n = Number(x)
    if (isNumber(n)) {
      if (x < min || x > max) {
        throw new Error(`value ${x} must be between ${min} and ${max}`)
      }
      return n
    }
  }
  throw new Error(`value ${x} is not a number`)
}

/**
 * @param {any | bigint} x
 * @returns {number}
 */
export const toPositiveNumber = (x) => toNumber(x, 0)

/**
 * @param {any} x
 * @param {bigint | number} [min]
 * @param {bigint | number} [max]
 * @returns {bigint}
 */
export const toBigInt = (x, min, max) => {
  if (typeof x === 'undefined') throw new Error('value is required')

  const n = BigInt(x)
  if (min !== undefined && n < BigInt(min)) {
    throw new Error(`value ${x} must be at least ${min}`)
  }

  if (max !== undefined && n > BigInt(max)) {
    throw new Error(`value ${x} must be at most ${max}`)
  }

  return n
}

/**
 * @param {number|bigint} x
 * @returns {bigint}
 */
export const toPositiveBigInt = (x) => {
  return toBigInt(x, 0)
}

/**
 * @param {number|bigint} x
 * @returns {number|bigint}
 */
export const toPositive = (x) => {
  if (typeof x === 'bigint') return toPositiveBigInt(x)
  return toPositiveNumber(x)
}

/**
 * Truncates a string intelligently, trying to keep natural breaks
 * @param {string} str - The string to truncate
 * @param {number} maxLength - Maximum length of the result
 * @param {string} [suffix='...'] - String to append when truncated
 * @returns {string} Truncated string
 */
export const truncateString = (str, maxLength, suffix = ' ...') => {
  if (!str || str.length <= maxLength) return str

  const effectiveLength = maxLength - suffix.length

  // Split into paragraphs and accumulate until we exceed the limit
  const paragraphs = str.split(/\n\n+/)
  let result = ''
  for (const paragraph of paragraphs) {
    if ((result + paragraph).length > effectiveLength) {
      // If this is the first paragraph and it's too long,
      // fall back to sentence/word breaking
      if (!result) {
        // Try to break at sentence
        const sentenceBreak = paragraph.slice(0, effectiveLength).match(/[.!?]\s+[A-Z]/g)
        if (sentenceBreak) {
          const lastBreak = paragraph.lastIndexOf(sentenceBreak[sentenceBreak.length - 1], effectiveLength)
          if (lastBreak > effectiveLength / 2) {
            return paragraph.slice(0, lastBreak + 1) + suffix
          }
        }

        // Try to break at word
        const wordBreak = paragraph.lastIndexOf(' ', effectiveLength)
        if (wordBreak > 0) {
          return paragraph.slice(0, wordBreak) + suffix
        }

        // Fall back to character break
        return paragraph.slice(0, effectiveLength) + suffix
      }
      return result.trim() + suffix
    }
    result += (result ? '\n\n' : '') + paragraph
  }

  return result
}

export function formatBytes (bytes) {
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let index = 0
  while (bytes >= 1024 && index < units.length - 1) {
    bytes /= 1024
    index++
  }
  return `${bytes.toFixed(2)} ${units[index]}`
}

// Monero display helper (StasherNews). Internal amounts are BigInt piconeros
// (1e-12 XMR). Delegates to the decimal converter in api/monero/uri.js, which is
// the single source of truth for the piconeros->XMR string format.
export const piconerosToXmr = (piconeros, maxDecimals) => piconerosToXmrDecimal(piconeros, maxDecimals) + ' XMR'

export const piconerosToMXmr = (piconeros, maxDecimals) => piconerosToMillineroDecimal(piconeros, maxDecimals) + ' mXMR'

// Basis points (1/100 of a percent) as a whole-percent label; non-zero shares
// under 1% read '<1%' rather than a misleading 0%.
export const formatShare = bps => {
  const n = Number(bps)
  if (n > 0 && n < 100) return '<1%'
  return `${Math.round(n / 100)}%`
}

export const piconerosToMXmrDual = (piconeros) => `${piconerosToMXmr(piconeros)} (${piconerosToXmr(piconeros)})`

// Analytics growth series arrive in legacy sats (1 sat = 1000 piconeros);
// convert each bucket's values to numeric mXMR (sats / 1e6) for charting.
export const legacySatsSeriesToMXmr = series => series?.map(({ time, data }) => ({
  time,
  data: data.map(({ name, value }) => ({ name, value: Number(value) / 1e6 }))
}))

// legacy-sat conversion for the fee-button accumulator, which totals in sats
// (upstream-inherited; 1 sat = 1000 piconeros). Display-side inverse is
// legacySatsToPiconeros in components/fee-button.js. Truncates sub-sat
// piconeros — every real fee line is a whole multiple of 1000 piconeros.
export const piconerosToSats = piconeros => Number(BigInt(piconeros) / 1000n)

// Underpayment hint for gated fee/bounty payment views: null when nothing
// received yet or the expected total is covered, else a human line quoting
// what landed and what is still missing.
export function underpayHint (receivedPiconeros, expectedPiconeros) {
  if (typeof receivedPiconeros !== 'bigint' || typeof expectedPiconeros !== 'bigint') return null
  if (receivedPiconeros <= 0n || receivedPiconeros >= expectedPiconeros) return null
  const short = expectedPiconeros - receivedPiconeros
  return `payment detected but short — received ${piconerosToMXmr(receivedPiconeros)} of ${piconerosToMXmr(expectedPiconeros)}. Send ${piconerosToXmr(short)} (${piconerosToMXmr(short)}) to the same address to complete it.`
}

export function formatDaysHours (ms) {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return '<1h'
  const totalHours = Math.floor(ms / 3600000)
  if (totalHours >= 24) return `${Math.floor(totalHours / 24)}d ${totalHours % 24}h`
  if (totalHours > 0) return `${totalHours}hr`
  return '<1h'
}

// Inverse of piconerosToXmrDecimal (api/monero/uri.js). Parses a human XMR decimal
// string into BigInt piconeros. This is the one client-side boundary where a typed
// decimal becomes the project's internal BigInt (used by the tip modal amount input).
export function xmrToPiconeros (xmr) {
  if (typeof xmr !== 'string' && typeof xmr !== 'number') {
    throw new Error('xmrToPiconeros: amount must be a string or number')
  }
  const s = String(xmr).trim()
  if (!/^\d+(\.\d+)?$/.test(s)) {
    throw new Error('xmrToPiconeros: invalid XMR amount')
  }
  const [whole, frac = ''] = s.split('.')
  if (frac.length > 12) {
    throw new Error('xmrToPiconeros: XMR has more than 12 decimal places')
  }
  return BigInt(whole) * 10n ** 12n + BigInt(frac.padEnd(12, '0'))
}

// Converts a possibly-negative XMR filter value (number or string, exponent
// notation allowed) to BigInt piconeros, quantizing onto the filter grid
// (FILTER_STEP_XMR = 0.0001 XMR = 1e8 piconeros). Slider stepping (min + n*step)
// and snapToFilterGrid's own k*step multiplication emit doubles whose shortest
// decimal representation exceeds xmrToPiconeros' 12-decimal guard (e.g.
// -0.025500000000000002), so convert via integer grid steps instead of parsing
// the decimal string.
export function signedXmrToPiconeros (xmr) {
  const steps = Math.round(Number(xmr) / FILTER_STEP_XMR)
  return BigInt(steps) * 100000000n
}

// Exact XMR amount (string or number, signed, exponent notation allowed) ->
// BigInt piconeros for arbitrary amount entry (tip/boost/donate/bounty hints).
// Parses the decimal exactly when possible; otherwise rounds to the nearest
// piconero to absorb float representation noise. Unlike signedXmrToPiconeros
// this never snaps to the 0.0001 XMR filter grid, so off-grid amounts like
// 0.00123 keep their true value.
export function xmrAmountToPiconeros (xmr) {
  const s = String(xmr).trim()
  const neg = s.startsWith('-')
  const magnitude = neg ? s.slice(1) : s
  if (magnitude === '') throw new Error('xmrAmountToPiconeros: invalid XMR amount')
  let piconeros
  try {
    piconeros = xmrToPiconeros(magnitude)
  } catch {
    const n = Number(magnitude)
    if (!Number.isFinite(n)) throw new Error('xmrAmountToPiconeros: invalid XMR amount')
    piconeros = BigInt(Math.round(n * 1e12))
  }
  return neg ? -piconeros : piconeros
}

// Feed filters are edited with a Range whose step is FILTER_STEP_XMR and validated
// against server bounds of -0.1..+0.01 XMR. Snap a raw XMR value to that grid so
// values like 1e-11 (the legacy 10n piconeros default) collapse onto an accepted step.
export const FILTER_STEP_XMR = 0.0001
export function snapToFilterGrid (xmr) {
  return Math.round(xmr / FILTER_STEP_XMR) * FILTER_STEP_XMR
}

// Extracts the tx_amount query param from a monero: URI (built by buildMoneroUri
// in api/monero/uri.js) as BigInt piconeros, or null when absent/unparseable.
// Lets the UI quote the fee straight from the QR URI — the amount is never stale
// and works for logged-out (anon) posters who have no privates.
export function moneroUriAmountPiconeros (moneroUri) {
  if (typeof moneroUri !== 'string') return null
  const query = moneroUri.split('?')[1] ?? ''
  const amount = new URLSearchParams(query).get('tx_amount')
  if (amount === null) return null
  try {
    return xmrToPiconeros(amount)
  } catch {
    return null
  }
}

export function moneroUriAddress (moneroUri) {
  if (typeof moneroUri !== 'string' || !moneroUri.startsWith('monero:')) return null
  const rest = moneroUri.slice('monero:'.length)
  const address = rest.split('?')[0]
  return MONERO_ADDR_RE.test(address) ? address : null
}
